/**
 * ============================================createBaseQuery====================
 * API SERVICE: AccountsService (Consolidated)
 * ================================================================
 *
 * @service          accountsApi
 * @description      Consolidated API service for all accounts management features
 * @baseUrl          VITE_ACCOUNT_URL environment variable
 *
 * @endpoints
 * - GET   /global-summary-brokerage - Global brokerage summary data
 * - GET   /client-wise-dr-cr - Client-wise DR/CR analysis
 * - GET   /ageing - Client aging analysis and outstanding balances
 * - GET   /late-payment-charges - Late payment charges calculation
 * - GET   /risk-common-report - Risk assessment and compliance reports
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        Standard API rate limiting applies
 *
 * @caching
 * - Strategy: RTK Query automatic caching with feature-based tags
 * - Tags: ['GlobalSummary', 'ClientDrCr', 'Aging', 'LatePaymentCharges', 'Payout', 'RiskCommon']
 * - TTL: 5 minutes for financial data consistency
 *
 * @errorHandling
 * - Global handler: transformApiError for consistent error management
 * - Auth errors: Automatic session clearing and login redirect
 * - Network errors: User-friendly error messages with retry options
 *
 * @types
 * - All request/response types imported from @/types/accounts
 * - Centralized type management for better maintainability
 * - Consistent interface definitions across all endpoints
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer, createBaseQuery } from '@/lib/api/baseQuery';
import type {
  AgingAnalysisRequest,
  AgingAnalysisResponse,
  ClientDrCrRequest,
  ClientDrCrResponse,
  GlobalSummaryRequest,
  GlobalSummaryResponse,
  LatePaymentChargesRequest,
  LatePaymentChargesResponse,
  PayoutRequest,
  PayoutResponse,
  RiskCommonRequest,
  RiskCommonResponse,
} from '@/types/accounts';
import { PayoutApiResponse } from '@/types/accounts/Payout';

export const accountsApi = createApi({
  reducerPath: 'accountsApi',
  baseQuery: createBaseQuery(import.meta.env.VITE_ACCOUNT_URL),
  // baseQuery: createBaseQuery(import.meta.env.VITE_ACCOUNT_REPORTS_URL),
  tagTypes: [
    'GlobalSummary',
    'ClientDrCr',
    'Aging',
    'LatePaymentCharges',
    'Payout',
    'RiskCommon',
    'APUsers',
    'APUsersV2',
    'APSubmitPayout',
  ],
  endpoints: (builder) => ({
    // ================================================================
    // GLOBAL SUMMARY BROKERAGE ENDPOINT
    // ================================================================

    /**
     * Fetch global brokerage summary data with comprehensive analytics
     *
     * @endpoint GET /global-summary-brokerage
     * @param {GlobalSummaryRequest} requestBody - Query parameters and filters
     * @returns {GlobalSummaryResponse} Global summary data with analytics
     * @cache Provides 'GlobalSummary' tag for targeted invalidation
     */
    getGlobalSummary: builder.query<GlobalSummaryResponse, GlobalSummaryRequest>({
      query: (requestBody) => ({
        url: '/get-global-summary-brokerage-data',
        method: 'GET',
        // NOTE: GET requests cannot include a body. Using `params` ensures query string transmission.
        params: requestBody,
      }),
      providesTags: ['GlobalSummary'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Global Summary
      // ================================================================
      //
      // Purpose: Process and validate global summary API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: GlobalSummaryResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch global summary data');
        }
        return response;
      },

      // Use standardized error transformer with tag-specific context & toast
      transformErrorResponse: createApiErrorTransformer('GlobalSummary', false),
    }),

    // ================================================================
    // CLIENT-WISE DR/CR ENDPOINT
    // ================================================================

    /**
     * Fetch client-wise debit/credit analysis data
     *
     * @endpoint GET /client-wise-dr-cr
     * @param {ClientDrCrRequest} requestBody - Query parameters for DR/CR analysis
     * @returns {ClientDrCrResponse} Client-wise DR/CR data with balances
     * @cache Provides 'ClientDrCr' tag for targeted invalidation
     */
    getClientDrCr: builder.query<ClientDrCrResponse, ClientDrCrRequest>({
      query: (requestBody) => ({
        url: '/get-client-wise-drcr-data',
        method: 'GET',
        // Converted from body -> params to avoid browser Request construction error.
        params: requestBody,
      }),
      providesTags: ['ClientDrCr'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Client DR/CR
      // ================================================================
      //
      // Purpose: Process and validate client DR/CR API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: ClientDrCrResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch client DR/CR data');
        }
        return response;
      },

      transformErrorResponse: createApiErrorTransformer('ClientDrCr', false),
    }),

    // ================================================================
    // AGING ANALYSIS ENDPOINT
    // ================================================================

    /**
     * Fetch client aging analysis and outstanding balances
     *
     * @endpoint GET /ageing
     * @param {AgingAnalysisRequest} requestBody - Query parameters for aging analysis
     * @returns {AgingAnalysisResponse} Aging data with outstanding balance breakdown
     * @cache Provides 'Aging' tag for targeted invalidation
     */
    getAging: builder.query<AgingAnalysisResponse, AgingAnalysisRequest>({
      query: (requestBody) => ({
        url: '/get-ageing-data',
        method: 'GET',
        params: requestBody,
      }),
      providesTags: ['Aging'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Aging Analysis
      // ================================================================
      //
      // Purpose: Process and validate aging analysis API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: AgingAnalysisResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch aging analysis data');
        }
        return response;
      },

      transformErrorResponse: createApiErrorTransformer('Aging', false),
    }),

    // ================================================================
    // LATE PAYMENT CHARGES ENDPOINT
    // ================================================================

    /**
     * Calculate and fetch late payment charges data
     *
     * @endpoint GET /late-payment-charges
     * @param {LatePaymentChargesRequest} requestBody - Parameters for charges calculation
     * @returns {LatePaymentChargesResponse} Late payment charges with breakdown
     * @cache Provides 'LatePaymentCharges' tag for targeted invalidation
     */
    getLatePaymentCharges: builder.query<LatePaymentChargesResponse, LatePaymentChargesRequest>({
      query: (requestBody) => ({
        url: '/get-late-payment-charges-data',
        method: 'GET',
        params: requestBody,
      }),
      providesTags: ['LatePaymentCharges'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Late Payment Charges
      // ================================================================
      //
      // Purpose: Process and validate late payment charges API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: LatePaymentChargesResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch late payment charges data');
        }
        return response;
      },

      transformErrorResponse: createApiErrorTransformer('LatePaymentCharges', false),
    }),

    // ================================================================
    // PAYOUT ENDPOINT
    // ================================================================

    /**
     * Calculate and fetch payout data
     *
     * @endpoint GET /payout
     * @param {PayoutRequest} requestBody - Parameters for payout calculation
     * @returns {PayoutResponse} Payout data with breakdown
     * @cache Provides 'Payout' tag for targeted invalidation
     */
    getPayout: builder.query<PayoutResponse, PayoutRequest>({
      query: (requestBody) => ({
        url: '/get-payout-data',
        method: 'GET',
        params: requestBody,
      }),
      providesTags: ['Payout'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Payout
      // ================================================================
      //
      // Purpose: Process and validate payout API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: PayoutResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch payout data');
        }
        return response;
      },

      transformErrorResponse: createApiErrorTransformer('Payout', false),
    }),

    // ================================================================
    // RISK COMMON REPORT ENDPOINT
    // ================================================================

    /**
     * Fetch risk assessment and compliance report data
     *
     * @endpoint GET /risk-common-report
     * @param {RiskCommonRequest} requestBody - Parameters for risk assessment
     * @returns {RiskCommonResponse} Risk assessment data with compliance metrics
     * @cache Provides 'RiskCommon' tag for targeted invalidation
     */
    getRiskCommon: builder.query<RiskCommonResponse, RiskCommonRequest>({
      query: (requestBody) => ({
        url: '/get-risk-common-data',
        method: 'GET',
        params: requestBody,
      }),
      providesTags: ['RiskCommon'],
      keepUnusedDataFor: 300, // 5 minutes cache

      // ================================================================
      // RESPONSE TRANSFORMATION - Risk Common Report
      // ================================================================
      //
      // Purpose: Process and validate risk assessment API responses
      // Validation: Check for server-side error indicators
      // Error handling: Throw errors for failed responses
      // ================================================================

      transformResponse: (response: RiskCommonResponse) => {
        if (response.statusCode >= 400) {
          throw new Error(response.message || 'Failed to fetch risk common report data');
        }
        return response;
      },

      transformErrorResponse: createApiErrorTransformer('RiskCommon', false),
    }),

    // ================================================================
    // FETCH AP USERS ENDPOINT
    // ================================================================

    /**
     * Fetch AP users for payout generation
     *
     * @endpoint GET /ap/fetch-ap-users
     * @returns List of AP users with client details
     * @cache Provides 'APUsers' tag for targeted invalidation
     */
    fetchApUsers: builder.query<any, void>({
      query: () => ({
        url: `${import.meta.env.VITE_PAYOUT}/v2/fund/ap/fetch-ap-users`,
        method: 'GET',
      }),
      providesTags: ['APUsers'],
      keepUnusedDataFor: 300,
    }),

    // ================================================================
    // FETCH AP USERS V2 ENDPOINT
    // ================================================================

    /**
     * Fetch AP users V2 for submit payout
     *
     * @endpoint GET /v2/fund/ap/fetch-ap-users
     * @returns List of AP users with fund details
     * @cache Provides 'APUsersV2' tag for targeted invalidation
     */
    fetchApUsersV2: builder.query<any, void>({
      query: () => ({
        url: `${import.meta.env.VITE_PAYOUT}/v2/fund/ap/fetch-ap-users`,
        method: 'GET',
      }),
      providesTags: ['APUsersV2'],
      keepUnusedDataFor: 300,
    }),

    // ================================================================
    // FETCH AP SUBMIT PAYOUT ENDPOINT
    // ================================================================

    /**
     * Fetch AP submit payout data for submit payout tab
     *
     * @endpoint GET /v2/fund/ap/redis-fetch-withdrawable-amounts
     * @param {number} page - Page number for pagination (1-indexed)
     * @param {number} pageSize - Number of items per page
     * @returns List of payout data for submission with pagination
     * @cache Provides 'APSubmitPayout' tag for targeted invalidation
     */
    fetchApSubmitPayout: builder.query<
      PayoutApiResponse,
      { page: number; pageSize: number; search?: string }
    >({
      query: ({ page = 1, pageSize = 5, search = '' }) => {
        const params: any = { page, pageSize };

        if (search && search.trim()) {
          params.search = String(search);
        }

        return {
          url: `${import.meta.env.VITE_PAYOUT}/v2/fund/ap/fetch-withdrawable-amounts`,
          method: 'GET',
          params,
        };
      },
      providesTags: ['APSubmitPayout'],
      keepUnusedDataFor: 300,
    }),

    // ================================================================
    // FETCH WITHDRAWABLE AMOUNTS ENDPOINT
    // ================================================================

    /**
     * Fetch withdrawable amounts for selected client IDs
     *
     * @endpoint POST /v2/fund/ap/fetch-withdrawable-amounts
     * @param {string} clientCodes - Comma-separated client codes (e.g., "A0031,A0032,A0033")
     * @returns Withdrawable amounts data for selected clients
     * @cache Invalidates 'APUsersV2' and 'APSubmitPayout' tags for fresh data
     */
    fetchWithdrawableAmounts: builder.mutation<any, string>({
      query: (clientCodes) => ({
        url: `${import.meta.env.VITE_PAYOUT}/v2/fund/ap/fetch-withdrawable-amounts`,
        method: 'POST',
        body: { clientCodes },
      }),
      invalidatesTags: ['APUsersV2', 'APSubmitPayout'],
    }),

    // ================================================================
    // SUBMIT PAYOUT ENDPOINT
    // ================================================================

    /**
     * Execute AP payout for selected users
     *
     * @endpoint POST /v2/fund/ap/execute-payout
     * @param {Array<{segments: string; amount: number; accNO: string|null; clientCode: string}>} payoutData
     * @returns Submission response from execute-payout endpoint
     */
    submitPayout: builder.mutation<any, any[]>({
      query: (payoutData) => ({
        url: `${import.meta.env.VITE_PAYOUT}/v2/fund/ap/execute-payout`,
        method: 'POST',
        body: payoutData,
      }),
      invalidatesTags: ['APUsersV2', 'Payout', 'APSubmitPayout'],
    }),
  }),
});

// ================================================================
// EXPORTED HOOKS FOR REACT COMPONENTS
// ================================================================
//
// Auto-generated hooks for use in React components
// Naming convention: use[EndpointName]Query
// Provides loading states, error handling, and caching automatically
// ================================================================

export const {
  useGetGlobalSummaryQuery,
  useLazyGetGlobalSummaryQuery,
  useGetClientDrCrQuery,
  useLazyGetClientDrCrQuery,
  useGetAgingQuery,
  useLazyGetAgingQuery,
  useGetLatePaymentChargesQuery,
  useLazyGetLatePaymentChargesQuery,
  useGetPayoutQuery,
  useLazyGetPayoutQuery,
  useGetRiskCommonQuery,
  useLazyGetRiskCommonQuery,
  useFetchApUsersQuery,
  useLazyFetchApUsersQuery,
  useFetchApUsersV2Query,
  useLazyFetchApUsersV2Query,
  useFetchApSubmitPayoutQuery,
  useLazyFetchApSubmitPayoutQuery,
  useFetchWithdrawableAmountsMutation,
  useSubmitPayoutMutation,
} = accountsApi;

/**
 * ================================================================
 * END OF SERVICE: AccountsService (Consolidated)
 * ================================================================
 *
 * @exports          accountsApi, all account-related query hooks
 * @dependencies     RTK Query, base query configuration, account types
 * @usedBy           All account pages and components
 *
 * @maintenance
 * - Cache strategy: Review TTL settings based on data freshness needs
 * - Error handling: Monitor error rates and adjust retry logic per endpoint
 * - Performance: Track query performance for each endpoint individually
 * - Tag management: Use specific tags for targeted cache invalidation
 *
 * @security
 * - Authentication: Handled by createBaseQuery configuration
 * - Data validation: Server response validation in each transformResponse
 * - Error exposure: Safe error messages via global transformApiError
 *
 * @consolidation
 * - Single service: Reduces import complexity and improves maintainability
 * - Consistent patterns: All endpoints follow same structure and error handling
 * - Tag isolation: Each endpoint has its own cache tag for precise invalidation
 * - Type safety: Full TypeScript support with centralized type imports
 *
 * ================================================================
 */
